import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormLabel from '@material-ui/core/FormLabel';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import WarningIcon from '@material-ui/icons/Warning';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { surgeryService } from '../../../services/surgeryService';
import { PathwayBadge, StatusBadge } from '../shared';
import DayCaseCheckIn from './DayCaseCheckIn';
import DayCaseProcedure from './DayCaseProcedure';
import ConvertToInpatientDialog from './ConvertToInpatientDialog';

const STEPS = ['Check-In', 'Procedure', 'Observation', 'Discharge'];

const DISCHARGE_ITEMS = [
  'Stable vitals for 30 minutes',
  'Alert and oriented',
  'Pain controlled (score < 4)',
  'No active bleeding',
  'Tolerated oral fluids',
  'Able to walk unassisted',
  'Companion present for escort',
  'Wound care instructions explained',
  'Medications dispensed and explained',
  'Follow-up appointment scheduled',
];

const createInitialObsRows = () => [
  { time: '15 min', hr: '', bp: '', spo2: '', pain: '', consciousness: 'Alert', wound: 'Dry', notes: '' },
  { time: '30 min', hr: '', bp: '', spo2: '', pain: '', consciousness: 'Alert', wound: 'Dry', notes: '' },
  { time: '60 min', hr: '', bp: '', spo2: '', pain: '', consciousness: 'Alert', wound: 'Dry', notes: '' },
];


const useStyles = makeStyles((theme) => ({
  root: { margin: 22 },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    padding: '16px 20px',
    marginBottom: 16,
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  patientInfo: { flex: 1, minWidth: 200 },
  patientName: { fontSize: '1.1rem', fontWeight: 700, color: '#333' },
  patientNumber: { fontSize: '0.8rem', color: '#888' },
  procedureName: { fontSize: '0.95rem', fontWeight: 600, color: '#555', minWidth: 180 },
  badgeGroup: { display: 'flex', gap: 8, alignItems: 'center' },
  metaText: { fontSize: '0.82rem', color: '#666' },
  stepperPaper: { padding: '8px 20px', marginBottom: 16, borderRadius: 8 },
  contentPaper: { padding: 24, marginBottom: 16, borderRadius: 8, minHeight: 400 },
  escalateBtn: {
    backgroundColor: '#D32F2F',
    color: '#fff',
    fontWeight: 600,
    marginBottom: 16,
    '&:hover': { backgroundColor: '#B71C1C' },
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
  },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 600, color: '#333', marginBottom: 16 },
  obsTable: { marginBottom: 24 },
  obsInput: { width: 70 },
  obsSelect: { width: 110, fontSize: '0.82rem' },
  obsNotes: { width: 140 },
  addRowBtn: { marginTop: 8, marginBottom: 24 },
  radioSection: {
    marginBottom: 16,
    padding: '12px 16px',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    backgroundColor: '#fafafa',
  },
  radioLabel: { fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 },
  obsTimer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    backgroundColor: '#E3F2FD',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#1565C0',
    marginBottom: 16,
  },
  dischargeSection: { marginTop: 8 },
  checkRow: { marginBottom: 4 },
  dischargeBtn: {
    marginTop: 16,
    backgroundColor: '#388E3C',
    color: '#fff',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#2E7D32' },
    '&.Mui-disabled': { backgroundColor: '#ccc', color: '#888' },
  },
  followUpPicker: { marginTop: 12, marginBottom: 8 },
  placeholderBox: {
    padding: 32,
    textAlign: 'center',
    color: '#999',
    border: '2px dashed #e0e0e0',
    borderRadius: 8,
    minHeight: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const OutpatientSurgeryPage = ({ match }) => {
  const classes = useStyles();
  const surgeryId = match && match.params ? match.params.surgeryId : null;
  const [surgery, setSurgery] = useState(null);
  const [surgeryLoading, setSurgeryLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [checkInData, setCheckInData] = useState({
    arrived: false,
    arrivalTime: null,
    identityVerified: false,
    fastingConfirmed: false,
    fastingHours: '',
    consentSigned: true,
    preOpComplete: true,
    bp: '',
    hr: '',
    spo2: '',
    temp: '',
    allergiesConfirmed: false,
    escortName: '',
    escortContact: '',
    belongingsSecured: false,
  });
  const [procedureData, setProcedureData] = useState({});
  const [observationData, setObservationData] = useState({
    rows: createInitialObsRows(),
    fluidTolerance: '',
    nausea: '',
    siteCheck: '',
  });
  const [dischargeData, setDischargeData] = useState({
    checks: new Array(DISCHARGE_ITEMS.length).fill(false),
    followUpDate: '',
  });
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [obsElapsed, setObsElapsed] = useState(0);

  // Observation timer
  useEffect(() => {
    let timer;
    if (activeStep === 2) {
      timer = setInterval(() => setObsElapsed((p) => p + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [activeStep]);

  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.viewSurgery({ surgery_id: surgeryId }).then((res) => {
      const d = res.data || {};
      setSurgery({
        id: d.surgery_id || d.id || surgeryId,
        patientName: d.patient_name || `${d.patient_first_name || ''} ${d.patient_last_name || ''}`.trim() || d.patient_number,
        patientNumber: d.patient_number || '',
        procedure: d.service_name || d.procedure || '',
        pathway: d.pathway || 'outpatient',
        status: d.status,
        doctor: d.lead_surgeon || '',
        theatre: d.theatre_name || '',
        anaesthesiaType: d.anaesthesia_type || '',
        scheduledDate: d.operation_date || '',
      });
    }).catch(() => setSurgery(null))
      .finally(() => setSurgeryLoading(false));
  }, [surgeryId]);

  const formatTimer = (secs) => {
    const h = String(Math.floor(secs / 3600)).padStart(2, '0');
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const handleCheckInChange = (field, value) => {
    setCheckInData((prev) => ({ ...prev, [field]: value }));
  };

  const handleObsRowChange = (rowIdx, field, value) => {
    setObservationData((prev) => {
      const rows = [...prev.rows];
      rows[rowIdx] = { ...rows[rowIdx], [field]: value };
      return { ...prev, rows };
    });
  };

  const handleAddObsRow = () => {
    const elapsed = formatTimer(obsElapsed);
    setObservationData((prev) => ({
      ...prev,
      rows: [
        ...prev.rows,
        { time: elapsed, hr: '', bp: '', spo2: '', pain: '', consciousness: 'Alert', wound: 'Dry', notes: '' },
      ],
    }));
  };

  const handleDischargeCheck = (idx, checked) => {
    setDischargeData((prev) => {
      const checks = [...prev.checks];
      checks[idx] = checked;
      return { ...prev, checks };
    });
  };

  const allDischargeChecked = dischargeData.checks.every(Boolean);

  const handleNext = async () => {
    try {
      if (activeStep === 0) {
        await surgeryService.dayCaseCheckIn(surgery.id, checkInData);
      } else if (activeStep === 2) {
        await surgeryService.saveDayCaseObs(surgery.id, observationData);
      }
    } catch (err) {
      // API not yet wired — continue with mock flow
    }
    setActiveStep((prev) => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleDischarge = async () => {
    try {
      await surgeryService.dischargeDayCase(surgery.id, { ...dischargeData, observationData });
    } catch (err) {
      // API not yet wired
    }
    alert('Patient discharged successfully');
  };

  const handleConvertConfirm = (data) => {
    setShowConvertDialog(false);
    alert('Converted to inpatient admission');
  };

  // ─── Observation Step Content ─────────────────────────────────────────
  const renderObservation = () => (
    <div>
      <Typography className={classes.sectionTitle}>Post-Procedure Observation</Typography>

      <div className={classes.obsTimer}>
        Observation Time: {formatTimer(obsElapsed)}
      </div>

      {/* Vitals Table */}
      <div className={classes.obsTable}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>HR</TableCell>
              <TableCell>BP</TableCell>
              <TableCell>SpO2</TableCell>
              <TableCell>Pain (1-10)</TableCell>
              <TableCell>Consciousness</TableCell>
              <TableCell>Wound</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {observationData.rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.time}</TableCell>
                <TableCell>
                  <TextField
                    className={classes.obsInput}
                    size="small"
                    variant="outlined"
                    value={row.hr}
                    onChange={(e) => handleObsRowChange(idx, 'hr', e.target.value)}
                    placeholder="bpm"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    className={classes.obsInput}
                    size="small"
                    variant="outlined"
                    value={row.bp}
                    onChange={(e) => handleObsRowChange(idx, 'bp', e.target.value)}
                    placeholder="mmHg"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    className={classes.obsInput}
                    size="small"
                    variant="outlined"
                    value={row.spo2}
                    onChange={(e) => handleObsRowChange(idx, 'spo2', e.target.value)}
                    placeholder="%"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    className={classes.obsInput}
                    size="small"
                    variant="outlined"
                    type="number"
                    inputProps={{ min: 1, max: 10 }}
                    value={row.pain}
                    onChange={(e) => handleObsRowChange(idx, 'pain', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    className={classes.obsSelect}
                    size="small"
                    variant="outlined"
                    value={row.consciousness}
                    onChange={(e) => handleObsRowChange(idx, 'consciousness', e.target.value)}
                  >
                    <MenuItem value="Alert">Alert</MenuItem>
                    <MenuItem value="Drowsy">Drowsy</MenuItem>
                    <MenuItem value="Responsive to voice">Responsive to voice</MenuItem>
                    <MenuItem value="Responsive to pain">Responsive to pain</MenuItem>
                    <MenuItem value="Unresponsive">Unresponsive</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    className={classes.obsSelect}
                    size="small"
                    variant="outlined"
                    value={row.wound}
                    onChange={(e) => handleObsRowChange(idx, 'wound', e.target.value)}
                  >
                    <MenuItem value="Dry">Dry</MenuItem>
                    <MenuItem value="Oozing">Oozing</MenuItem>
                    <MenuItem value="Bleeding">Bleeding</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    className={classes.obsNotes}
                    size="small"
                    variant="outlined"
                    value={row.notes}
                    onChange={(e) => handleObsRowChange(idx, 'notes', e.target.value)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button
          className={classes.addRowBtn}
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddObsRow}
        >
          Add Row
        </Button>
      </div>

      {/* Radio Assessments */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <div className={classes.radioSection}>
            <FormControl component="fieldset">
              <FormLabel className={classes.radioLabel}>Oral Fluid Tolerance</FormLabel>
              <RadioGroup
                value={observationData.fluidTolerance}
                onChange={(e) => setObservationData((p) => ({ ...p, fluidTolerance: e.target.value }))}
              >
                <FormControlLabel value="Tolerated" control={<Radio size="small" />} label="Tolerated" />
                <FormControlLabel value="Not tolerated" control={<Radio size="small" />} label="Not tolerated" />
              </RadioGroup>
            </FormControl>
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div className={classes.radioSection}>
            <FormControl component="fieldset">
              <FormLabel className={classes.radioLabel}>Nausea</FormLabel>
              <RadioGroup
                value={observationData.nausea}
                onChange={(e) => setObservationData((p) => ({ ...p, nausea: e.target.value }))}
              >
                <FormControlLabel value="None" control={<Radio size="small" />} label="None" />
                <FormControlLabel value="Mild" control={<Radio size="small" />} label="Mild" />
                <FormControlLabel value="Vomiting" control={<Radio size="small" />} label="Vomiting" />
              </RadioGroup>
            </FormControl>
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div className={classes.radioSection}>
            <FormControl component="fieldset">
              <FormLabel className={classes.radioLabel}>Surgical Site Check</FormLabel>
              <RadioGroup
                value={observationData.siteCheck}
                onChange={(e) => setObservationData((p) => ({ ...p, siteCheck: e.target.value }))}
              >
                <FormControlLabel value="Dry" control={<Radio size="small" />} label="Dry" />
                <FormControlLabel value="Oozing" control={<Radio size="small" />} label="Oozing" />
                <FormControlLabel
                  value="Bleeding — escalate"
                  control={<Radio size="small" color="secondary" />}
                  label="Bleeding — escalate"
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  // ─── Discharge Step Content ───────────────────────────────────────────
  const renderDischarge = () => (
    <div className={classes.dischargeSection}>
      <Typography className={classes.sectionTitle}>Discharge Checklist</Typography>
      {DISCHARGE_ITEMS.map((label, idx) => (
        <div key={idx} className={classes.checkRow}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dischargeData.checks[idx]}
                onChange={(e) => handleDischargeCheck(idx, e.target.checked)}
                color="primary"
              />
            }
            label={label}
          />
        </div>
      ))}

      {dischargeData.checks[DISCHARGE_ITEMS.length - 1] && (
        <TextField
          className={classes.followUpPicker}
          label="Follow-up Date"
          type="date"
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dischargeData.followUpDate}
          onChange={(e) => setDischargeData((p) => ({ ...p, followUpDate: e.target.value }))}
          fullWidth
          style={{ maxWidth: 260 }}
        />
      )}

      <div>
        <Button
          className={classes.dischargeBtn}
          variant="contained"
          disabled={!allDischargeChecked}
          onClick={handleDischarge}
        >
          Discharge Home
        </Button>
      </div>
    </div>
  );

  // ─── Step Content Router ──────────────────────────────────────────────
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <DayCaseCheckIn surgery={surgery} data={checkInData} onChange={handleCheckInChange} />;
      case 1:
        return <DayCaseProcedure surgery={surgery} />;
      case 2:
        return renderObservation();
      case 3:
        return renderDischarge();
      default:
        return null;
    }
  };

  if (surgeryLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 60 }}>
        <CircularProgress />
      </div>
    );
  }

  if (!surgery) {
    return (
      <div>
        <PageTitle title="Outpatient Surgery" />
        <SubHeader title="Surgery" subTitle="Outpatient Surgery" />
        <Paper style={{ margin: 22, padding: 40, textAlign: 'center' }} elevation={1}>
          <Typography variant="h6" style={{ color: '#999' }}>Surgery not found</Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Outpatient Surgery" />
      <SubHeader title="Surgery" subTitle="Outpatient Surgery" />

      <div className={classes.root}>
        {/* ── Top Bar ── */}
        <div className={classes.topBar}>
          <div className={classes.patientInfo}>
            <Typography className={classes.patientName}>{surgery.patientName}</Typography>
            <Typography className={classes.patientNumber}>{surgery.patientNumber}</Typography>
          </div>
          <Typography className={classes.procedureName}>{surgery.procedure}</Typography>
          <div className={classes.badgeGroup}>
            <PathwayBadge pathway={surgery.pathway} />
            <StatusBadge status={surgery.status} />
          </div>
          <div>
            <Typography className={classes.metaText}>{surgery.doctor}</Typography>
            <Typography className={classes.metaText}>{surgery.theatre}</Typography>
          </div>
        </div>

        {/* ── Stepper ── */}
        <Paper className={classes.stepperPaper} elevation={1}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* ── Escalate Button ── */}
        {activeStep >= 1 && (
          <Button
            className={classes.escalateBtn}
            variant="contained"
            startIcon={<WarningIcon />}
            onClick={() => setShowConvertDialog(true)}
            fullWidth
          >
            Escalate &mdash; Convert to Inpatient
          </Button>
        )}

        {/* ── Content Area ── */}
        <Paper className={classes.contentPaper} elevation={1}>
          {renderStepContent()}
        </Paper>

        {/* ── Navigation ── */}
        <div className={classes.navRow}>
          <Button variant="outlined" disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          {activeStep < 3 && (
            <Button variant="contained" color="primary" onClick={handleNext}>
              Next
            </Button>
          )}
        </div>

        {/* ── Convert Dialog ── */}
        <ConvertToInpatientDialog
          open={showConvertDialog}
          onClose={() => setShowConvertDialog(false)}
          onConfirm={handleConvertConfirm}
          surgeryId={surgery.id}
        />
      </div>
    </div>
  );
};

export default OutpatientSurgeryPage;
